/**
 * Kanban board color constants supporting both light and dark modes
 *
 * Dark mode uses #000000 (true black) as the primary background throughout.
 * Light mode uses #FFFFFF (white) with subtle gray tones for elevation and depth.
 *
 * These constants ensure the kanban board follows the app-wide theme system.
 *
 * References:
 * - app/_layout.tsx: Uses "black" (#000000) for main backgrounds in dark mode
 * - config/colors.ts: Notes use #1C1C1E for card backgrounds
 * - tailwind.config.js: Background colors use true black system
 * - providers/ThemeProvider.tsx: App-wide theme system
 */

/**
 * Shared colors that are identical across both themes
 */
const COMMON_COLORS = {
  /**
   * Interactive elements - secondary (identical in both themes)
   */
  secondary: "#64748B", // Slate-500 - secondary buttons
  secondaryHover: "#475569", // Slate-600 - secondary hover
  secondaryPressed: "#334155", // Slate-700 - secondary pressed

  /**
   * State colors (identical in both themes)
   */
  success: "#45A268", // From config/colors.ts - success states
  successPressed: "#388754", // Darker green for pressed success states
  warning: "#E77828", // From config/colors.ts - warning states
  error: "#EF4444", // Red-500 - error states, danger actions

  /**
   * Accent colors (identical in both themes)
   */
  accentHover: "#8B5CF6", // Purple-500 - accent hover state
} as const;

/**
 * Theme-specific color configuration
 */
interface ThemeColors {
  mainBackground: string;
  columnBackground: string;
  cardBackground: string;
  columnHeader: string;
  border: string;
  borderLight: string;
  divider: string;
  primary: string;
  primaryHover: string;
  primaryPressed: string;
  primaryLight: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textDisabled: string;
  info: string;
  shadow: string;
  overlay: string;
  highlight: string;
  tagBackground: string;
  tagBackgroundActive: string;
  badgeBackground: string;
  filterDropdownBackground: string;
  filterItemHover: string;
  filterItemActive: string;
}

const DARK_MODE_COLORS: ThemeColors = {
  mainBackground: "#000000",
  columnBackground: "#000000",
  cardBackground: "#1C1C1E",
  columnHeader: "#2A2A39",
  border: "#3F3F46",
  borderLight: "#52525B",
  divider: "#525252",
  primary: "#3B82F6",
  primaryHover: "#2563EB",
  primaryPressed: "#1D4ED8",
  primaryLight: "#60A5FA",
  textPrimary: "#FFFFFF",
  textSecondary: "#9CA3AF",
  textMuted: "#71717A",
  textDisabled: "#52525B",
  info: "#3B82F6",
  shadow: "rgba(0, 0, 0, 0.6)",
  overlay: "rgba(0, 0, 0, 0.75)",
  highlight: "rgba(59, 130, 246, 0.1)",
  tagBackground: "#2A2A39",
  tagBackgroundActive: "#3B82F6",
  badgeBackground: "#6B7280",
  filterDropdownBackground: "#2A2A39",
  filterItemHover: "#3F3F46",
  filterItemActive: "#3B82F6",
} as const;

const LIGHT_MODE_COLORS: ThemeColors = {
  mainBackground: "#FFFFFF",
  columnBackground: "#F8F9FA",
  cardBackground: "#FFFFFF",
  columnHeader: "#F3F4F6",
  border: "#E5E7EB",
  borderLight: "#D1D5DB",
  divider: "#E5E7EB",
  primary: "#2563EB",
  primaryHover: "#1D4ED8",
  primaryPressed: "#1E40AF",
  primaryLight: "#3B82F6",
  textPrimary: "#111827",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  textDisabled: "#D1D5DB",
  info: "#2563EB",
  shadow: "rgba(0, 0, 0, 0.1)",
  overlay: "rgba(0, 0, 0, 0.5)",
  highlight: "rgba(37, 99, 235, 0.1)",
  tagBackground: "#F3F4F6",
  tagBackgroundActive: "#2563EB",
  badgeBackground: "#E5E7EB",
  filterDropdownBackground: "#FFFFFF",
  filterItemHover: "#F3F4F6",
  filterItemActive: "#2563EB",
} as const;

/**
 * Dark mode color palette
 */
export const KANBAN_COLORS_DARK = {
  ...DARK_MODE_COLORS,
  ...COMMON_COLORS,
} as const;

/**
 * Light mode color palette
 */
export const KANBAN_COLORS_LIGHT = {
  ...LIGHT_MODE_COLORS,
  ...COMMON_COLORS,
} as const;

/**
 * Type helper for kanban colors - supports both light and dark mode
 */
export type KanbanColors =
  | typeof KANBAN_COLORS_DARK
  | typeof KANBAN_COLORS_LIGHT;
/**
 *
 */
export type KanbanColor =
  (typeof KANBAN_COLORS_DARK)[keyof typeof KANBAN_COLORS_DARK];
